import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Avatar,
  Tooltip,
  IconButton,
  alpha,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Description as DocumentIcon,
  Public as PublicIcon,
  Lock as PrivateIcon,
  Event as EventIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  Visibility as ViewIcon,
  Security as SecurityIcon,
  OpenInNew as OpenInNewIcon,
  PictureAsPdf as PdfIcon,
  ErrorOutline as ErrorIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import axiosInstance from '../../../utils/axios';
import { useAuth } from '../../../contexts/AuthContext';
import { format } from 'date-fns';
import Header from '../../common/Header';
import Loading from '../../common/Loading';
import FeedbackMessage from '../../common/FeedbackMessage';
import ConfirmDeleteDialog from '../../common/ConfirmDeleteDialog';
import { SUCCESS } from '../../common/SaaSTokens';
import { canManageDocuments, canViewPrivateDocuments } from '../../../utils/documentPermissions';
import {
  injectDetailsPageStyles,
} from '../../common/DetailsPagePrimitives';
import SystemInformationSection from '../../common/SystemInformationSection';
import { formatSystemDateTime } from '../../../utils/systemDateFormat';
import {
  buildPdfEmbedSrc,
  downloadDocumentFile,
  fetchDocumentPreviewBlob,
  openBlobUrlInNewTab,
  openDocumentInNewTab,
} from '../../../utils/protectedFilePreview';

// ─── Design tokens ────────────────────────────────────────────────────────────
const NEON       = '#38BDF8';
const NEON2      = '#818CF8';
const NEON3      = '#34D399';
const CORAL      = '#F87171';
const TEXT_BODY  = '#E2E8F0';
const TEXT_MUTED = '#64748B';
const SURFACE_UP  = '#0f1f38';
const SURFACE_MID = '#0a1628';
const SURFACE_TOP = '#152640';
const B = { 600: '#2563EB', 700: '#1D4ED8', 900: '#1E3A8A' };

const Section = ({ title, icon: Icon, accent = NEON, delay = 0, children }) => (
  <Box sx={{
    borderRadius: '16px',
    border: `1px solid ${alpha(accent, 0.14)}`,
    background: `linear-gradient(160deg, ${alpha(SURFACE_UP, 0.75)} 0%, ${alpha(SURFACE_MID, 0.95)} 100%)`,
    overflow: 'hidden',
    animation: `fadeUp 0.4s ease ${delay}s both`,
    '@keyframes fadeUp': {
      from: { opacity: 0, transform: 'translateY(10px)' },
      to: { opacity: 1, transform: 'translateY(0)' },
    },
  }}>
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1.2,
      px: 2.5, py: 1.5,
      borderBottom: `1px solid ${alpha(accent, 0.1)}`,
      background: alpha(accent, 0.04),
    }}>
      <Box sx={{
        width: 26, height: 26, borderRadius: '7px',
        background: alpha(accent, 0.12),
        border: `1px solid ${alpha(accent, 0.22)}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon sx={{ fontSize: 13, color: accent }} />
      </Box>
      <Typography sx={{
        fontFamily: "'DM Sans', sans-serif", fontWeight: 700,
        fontSize: '0.78rem', color: alpha(TEXT_BODY, 0.85),
        letterSpacing: '0.07em', textTransform: 'uppercase',
      }}>
        {title}
      </Typography>
    </Box>
    <Box sx={{ p: 2 }}>{children}</Box>
  </Box>
);

const InfoRow = ({ icon: Icon, label, value, color = NEON, emptyLabel = 'Not specified' }) => {
  const display = value || emptyLabel;
  const isEmpty = !value;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.9, px: 1.2, borderRadius: '9px' }}>
      <Box sx={{
        width: 30, height: 30, borderRadius: '8px', flexShrink: 0,
        background: alpha(color, isEmpty ? 0.05 : 0.1),
        border: `1px solid ${alpha(color, isEmpty ? 0.1 : 0.18)}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon sx={{ fontSize: 14, color: isEmpty ? TEXT_MUTED : alpha(color, 0.85) }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: '0.65rem', color: TEXT_MUTED, fontFamily: "'DM Sans', sans-serif", textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: 600 }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: '0.88rem', color: isEmpty ? TEXT_MUTED : TEXT_BODY, fontStyle: isEmpty ? 'italic' : 'normal', fontFamily: "'DM Sans', sans-serif", fontWeight: isEmpty ? 400 : 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {display}
        </Typography>
      </Box>
    </Box>
  );
};

const MetricCard = ({ label, value, icon: Icon, color = NEON, sub, onClick }) => (
  <Box
    component={onClick ? 'button' : 'div'}
    type={onClick ? 'button' : undefined}
    onClick={onClick}
    sx={{
    position: 'relative',
    borderRadius: '14px', px: 2.5, py: 2,
    width: '100%',
    textAlign: 'left',
    background: `linear-gradient(145deg, ${alpha(color, 0.09)} 0%, ${alpha(SURFACE_UP, 0.6)} 100%)`,
    border: `1px solid ${alpha(color, 0.2)}`,
    boxShadow: `0 0 24px ${alpha(color, 0.07)}, inset 0 1px 0 ${alpha(color, 0.1)}`,
    overflow: 'hidden',
    cursor: onClick ? 'pointer' : 'default',
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease, transform 0.15s ease',
    ...(onClick && {
      '&:hover': {
        borderColor: alpha(color, 0.45),
        boxShadow: `0 0 20px ${alpha(color, 0.15)}`,
        transform: 'translateY(-1px)',
      },
    }),
  }}>
    <Box sx={{ position: 'absolute', top: -16, right: -16, width: 70, height: 70, borderRadius: '50%', background: alpha(color, 0.08), filter: 'blur(18px)', pointerEvents: 'none' }} />
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.8 }}>
      {Icon && <Icon sx={{ fontSize: 13, color: alpha(color, 0.75) }} />}
      <Typography sx={{ fontSize: '0.67rem', fontFamily: "'DM Sans', sans-serif", color: TEXT_MUTED, letterSpacing: '0.09em', textTransform: 'uppercase', fontWeight: 600 }}>
        {label}
      </Typography>
    </Box>
    <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, fontFamily: "'Rajdhani', sans-serif", color, letterSpacing: 0.3, lineHeight: 1.15 }}>
      {value || '—'}
    </Typography>
    {sub && <Typography sx={{ fontSize: '0.7rem', color: TEXT_MUTED, fontFamily: "'DM Sans', sans-serif", mt: 0.4 }}>{sub}</Typography>}
  </Box>
);

// ─── ActionButtons ────────────────────────────────────────────────────────────
const ActionButtons = ({ canManageDocuments, onEdit, onDelete, onDownload }) => (
  <Box sx={{ display: 'flex', gap: 1.5 }}>
    <Button
      startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
      onClick={onDownload}
      sx={{
        borderRadius: '10px', px: 2.5, py: 0.7,
        fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.85rem',
        background: 'transparent', border: `1.5px solid ${alpha(NEON, 0.6)}`,
        color: NEON, transition: 'all 0.2s ease',
        '&:hover': { background: alpha(NEON, 0.08), borderColor: NEON, boxShadow: `0 0 12px ${alpha(NEON, 0.2)}` },
      }}
    >
      Download
    </Button>
    {canManageDocuments && (
      <>
        <Button
          startIcon={<EditIcon sx={{ fontSize: 16 }} />}
          onClick={onEdit}
          sx={{
            borderRadius: '10px', px: 2.2, py: 0.65,
            fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.83rem',
            background: 'transparent', border: `1.5px solid ${alpha(NEON, 0.55)}`,
            color: NEON, transition: 'all 0.2s',
            '&:hover': { background: alpha(NEON, 0.08), borderColor: NEON, boxShadow: `0 0 14px ${alpha(NEON, 0.2)}` },
          }}
        >
          Edit
        </Button>
        <Button
          startIcon={<DeleteIcon sx={{ fontSize: 16 }} />}
          onClick={onDelete}
          sx={{
            borderRadius: '10px', px: 2.2, py: 0.65,
            fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.83rem',
            background: 'transparent', border: `1.5px solid ${alpha(CORAL, 0.55)}`,
            color: CORAL, transition: 'all 0.2s',
            '&:hover': { background: alpha(CORAL, 0.08), borderColor: CORAL, boxShadow: `0 0 14px ${alpha(CORAL, 0.2)}` },
          }}
        >
          Delete
        </Button>
      </>
    )}
  </Box>
);

// ─── DocumentPreviewSection ───────────────────────────────────────────────────
const DocumentPreviewSection = ({
  documentData,
  documentPreviewUrl,
  previewLoading,
  previewFailed,
  onDownload,
  onRetryPreview,
  onOpenInTab,
}) => {
  const [iframeError, setIframeError]   = useState(false);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const iframeRef = useRef(null);

  useEffect(() => {
    setIframeError(false);
    setIframeLoaded(false);
  }, [documentPreviewUrl]);

  const handleOpenInTab = () => {
    onOpenInTab?.();
  };

  const handleRefresh = () => {
    setIframeError(false);
    setIframeLoaded(false);
    if (iframeRef.current) {
      const src = iframeRef.current.src;
      iframeRef.current.src = '';
      setTimeout(() => { iframeRef.current.src = src; }, 50);
    }
  };

  const showViewer = Boolean(documentPreviewUrl) || previewLoading;

  return (
    <Section title="Document Preview" icon={ViewIcon} delay={0.18} accent={NEON}>
      {showViewer ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>

          {/* ── Viewer toolbar ── */}
          <Box sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            px: 2, py: 1.25,
            borderRadius: '10px 10px 0 0',
            background: alpha(SURFACE_UP, 0.9),
            border: `1px solid ${alpha(NEON, 0.14)}`,
            borderBottom: 'none',
            backdropFilter: 'blur(8px)',
          }}>
            {/* Left: file badge */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0, flex: 1 }}>
              <Box sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 30, height: 30, borderRadius: '7px',
                background: `linear-gradient(135deg, ${alpha(NEON, 0.25)}, ${alpha(NEON2, 0.08)})`,
                border: `1px solid ${alpha(NEON, 0.3)}`,
              }}>
                <PdfIcon sx={{ fontSize: 16, color: NEON }} />
              </Box>
              <Box>
                <Typography sx={{
                  fontFamily: "'DM Sans', sans-serif", fontSize: '0.82rem',
                  fontWeight: 600, color: TEXT_BODY,
                  maxWidth: { xs: 140, sm: 280, md: 420 },
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {documentData.document_name}
                </Typography>
                <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.62rem', color: alpha(TEXT_MUTED, 0.7), letterSpacing: 0.5 }}>
                  PDF Document
                </Typography>
              </Box>
            </Box>

            {/* Right: action buttons */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0, ml: 1 }}>
              <Tooltip title="Refresh" placement="top">
                <IconButton onClick={handleRefresh} size="small" sx={{
                  color: TEXT_MUTED, borderRadius: '7px',
                  '&:hover': { background: alpha(NEON, 0.08), color: NEON },
                }}>
                  <RefreshIcon sx={{ fontSize: 17 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Open in new tab" placement="top">
                <IconButton onClick={handleOpenInTab} size="small" sx={{
                  color: TEXT_MUTED, borderRadius: '7px',
                  '&:hover': { background: alpha(NEON, 0.08), color: NEON },
                }}>
                  <OpenInNewIcon sx={{ fontSize: 17 }} />
                </IconButton>
              </Tooltip>
              <Button
                size="small"
                startIcon={<DownloadIcon sx={{ fontSize: 14 }} />}
                onClick={onDownload}
                sx={{
                  px: 1.5, py: 0.4, ml: 0.5, borderRadius: '7px',
                  fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.78rem',
                  whiteSpace: 'nowrap', flexShrink: 0,
                  background: alpha(NEON, 0.1), border: `1px solid ${alpha(NEON, 0.35)}`,
                  color: NEON, transition: 'all 0.2s ease',
                  '&:hover': { background: alpha(NEON, 0.18), borderColor: NEON },
                }}
              >
                Download
              </Button>
            </Box>
          </Box>

          {/* ── iframe viewer (fixed height) ── */}
          <Box sx={{
            position: 'relative',
            height: { xs: 460, md: 640 },
            borderRadius: '0 0 10px 10px',
            overflow: 'hidden',
            border: `1px solid ${alpha(NEON, 0.14)}`,
            background: '#0a0e1a',
          }}>
            {/* Loading shimmer */}
            {(previewLoading || (!iframeLoaded && !iframeError)) && (
              <Box sx={{
                position: 'absolute', inset: 0, zIndex: 2,
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center', gap: 2,
                background: '#0a0e1a',
              }}>
                <Box sx={{
                  width: 48, height: 48, borderRadius: '12px',
                  background: `linear-gradient(135deg, ${alpha(NEON, 0.2)}, ${alpha(NEON2, 0.05)})`,
                  border: `1px solid ${alpha(NEON, 0.25)}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  animation: 'pulse 1.5s ease-in-out infinite',
                  '@keyframes pulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.4 } },
                }}>
                  <PdfIcon sx={{ fontSize: 22, color: NEON }} />
                </Box>
                <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.82rem', color: alpha(TEXT_MUTED, 0.7) }}>
                  Loading document...
                </Typography>
              </Box>
            )}

            {/* Error state */}
            {iframeError && (
              <Box sx={{
                position: 'absolute', inset: 0, zIndex: 2,
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center', gap: 2.5,
                background: '#0a0e1a',
              }}>
                <Box sx={{
                  width: 56, height: 56, borderRadius: '14px',
                  background: alpha('#F87171', 0.1), border: `1px solid ${alpha('#F87171', 0.25)}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <ErrorIcon sx={{ fontSize: 26, color: '#F87171' }} />
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.9rem', color: TEXT_BODY, mb: 0.5 }}>
                    Preview unavailable
                  </Typography>
                  <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.8rem', color: TEXT_MUTED }}>
                    The document could not be rendered in the browser
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <Button size="small" startIcon={<RefreshIcon sx={{ fontSize: 14 }} />} onClick={handleRefresh}
                    sx={{
                      px: 2, py: 0.6, borderRadius: '8px',
                      fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.8rem',
                      border: `1px solid ${alpha(NEON, 0.4)}`, color: NEON, background: alpha(NEON, 0.06),
                      '&:hover': { background: alpha(NEON, 0.12) },
                    }}>
                    Retry
                  </Button>
                  <Button size="small" startIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />} onClick={handleOpenInTab}
                    sx={{
                      px: 2, py: 0.6, borderRadius: '8px',
                      fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.8rem',
                      border: `1px solid ${alpha(NEON, 0.4)}`, color: NEON, background: alpha(NEON, 0.06),
                      '&:hover': { background: alpha(NEON, 0.12) },
                    }}>
                    Open in tab
                  </Button>
                </Box>
              </Box>
            )}

            <iframe
              ref={iframeRef}
              src={documentPreviewUrl ? buildPdfEmbedSrc(documentPreviewUrl) : undefined}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                opacity: iframeLoaded && !iframeError ? 1 : 0,
                transition: 'opacity 0.3s ease',
              }}
              title={documentData.document_name}
              onLoad={() => {
                setIframeLoaded(true);
                setIframeError(false);
              }}
              onError={() => setIframeError(true)}
            />
          </Box>

          {/* ── Status bar ── */}
          <Box sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            px: 2, py: 0.85, mt: 0,
            borderRadius: '0 0 8px 8px',
            background: alpha(SURFACE_MID, 0.6),
            border: `1px solid ${alpha(NEON, 0.08)}`,
            borderTop: `1px solid ${alpha(NEON, 0.06)}`,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{
                width: 6, height: 6, borderRadius: '50%',
                background: iframeLoaded && !iframeError ? NEON3 : iframeError ? '#F87171' : NEON2,
                boxShadow: `0 0 5px ${iframeLoaded && !iframeError ? NEON3 : iframeError ? '#F87171' : NEON2}`,
              }} />
              <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.62rem', color: alpha(TEXT_MUTED, 0.6), letterSpacing: 0.4 }}>
                {previewLoading ? 'Loading...' : iframeLoaded && !iframeError ? 'Ready' : iframeError ? 'Load error' : 'Loading...'}
              </Typography>
            </Box>
            <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.62rem', color: alpha(TEXT_MUTED, 0.45), letterSpacing: 0.4 }}>
              PDF - Inline viewer
            </Typography>
          </Box>

        </Box>
      ) : (
        /* ── No preview / load failed ── */
        <Box sx={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          gap: 2.5, py: 6, px: 3, textAlign: 'center',
          borderRadius: '10px',
          border: `1px dashed ${alpha(NEON, 0.14)}`,
          background: alpha(SURFACE_UP, 0.3),
        }}>
          <Box sx={{
            width: 60, height: 60, borderRadius: '14px',
            background: alpha(SURFACE_UP, 0.8), border: `1px solid ${alpha(NEON, 0.14)}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <PdfIcon sx={{ fontSize: 26, color: alpha(TEXT_MUTED, 0.5) }} />
          </Box>
          <Box>
            <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.92rem', color: TEXT_BODY, mb: 0.5 }}>
              {previewFailed ? 'Preview unavailable' : 'No preview available'}
            </Typography>
            <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.8rem', color: TEXT_MUTED }}>
              {previewFailed
                ? 'The PDF could not be loaded. Try again or download the file.'
                : "This document doesn't have a PDF attached for preview."}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', justifyContent: 'center' }}>
            {previewFailed && onRetryPreview && (
              <Button
                startIcon={<RefreshIcon sx={{ fontSize: 15 }} />}
                onClick={onRetryPreview}
                sx={{
                  px: 2.5, py: 0.7, borderRadius: '9px',
                  fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.84rem',
                  background: alpha(NEON, 0.1), border: `1.5px solid ${alpha(NEON, 0.4)}`,
                  color: NEON, transition: 'all 0.2s ease',
                  '&:hover': { background: alpha(NEON, 0.18), borderColor: NEON },
                }}
              >
                Retry
              </Button>
            )}
            <Button
              startIcon={<DownloadIcon sx={{ fontSize: 15 }} />}
              onClick={onDownload}
              sx={{
                px: 2.5, py: 0.7, borderRadius: '9px',
                fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.84rem',
                background: alpha(NEON, 0.1), border: `1.5px solid ${alpha(NEON, 0.4)}`,
                color: NEON, transition: 'all 0.2s ease',
                '&:hover': { background: alpha(NEON, 0.18), borderColor: NEON },
              }}
            >
              {previewFailed ? 'Download' : 'Download Instead'}
            </Button>
          </Box>
        </Box>
      )}
    </Section>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const DocumentDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const { user } = useAuth();

  const [documentData, setDocumentData] = useState(location.state?.document);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [documentPreviewUrl, setDocumentPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewFailed, setPreviewFailed] = useState(false);
  const [previewRetryKey, setPreviewRetryKey] = useState(0);

  const canManage      = canManageDocuments(user);
  const canViewPrivate = canViewPrivateDocuments(user);

  useEffect(() => { injectDetailsPageStyles(); }, []);

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    fetchDocument();
  }, [user, id, navigate]);

  useEffect(() => {
    if (!documentData?.document_pdf || !id) {
      setDocumentPreviewUrl(null);
      setPreviewLoading(false);
      setPreviewFailed(false);
      return undefined;
    }

    let cancelled = false;
    let objectUrl = null;

    const loadPreview = async () => {
      setPreviewLoading(true);
      setPreviewFailed(false);
      setDocumentPreviewUrl((prev) => {
        if (prev?.startsWith('blob:')) URL.revokeObjectURL(prev);
        return null;
      });
      try {
        const preview = await fetchDocumentPreviewBlob(id);
        objectUrl = preview.url;
        if (!cancelled) setDocumentPreviewUrl(preview.url);
        else URL.revokeObjectURL(objectUrl);
      } catch {
        if (!cancelled) {
          setDocumentPreviewUrl(null);
          setPreviewFailed(true);
        }
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    };

    loadPreview();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [documentData?.document_pdf, id, previewRetryKey]);

  const fetchDocument = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/documents/${id}`);
      const docData = response.data;
      if (docData.publicity === 'private' && !canViewPrivate) {
        navigate('/documents', { state: { error: 'You do not have permission to view this document.' } });
        return;
      }
      setDocumentData(docData);
      setError(null);
    } catch (err) {
      if (err.response?.status === 403) {
        navigate('/documents', { state: { error: 'You do not have permission to view this document.' } });
        return;
      }
      setError('Failed to load document details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDocument = async () => {
    if (!canManage) return;
    try {
      const docName = documentData?.document_name || 'Document';
      await axiosInstance.delete(`/documents/${id}`);
      setDeleteDialogOpen(false);
      setSuccessMessage(`Document "${docName}" has been deleted successfully!`);
      setTimeout(() => navigate('/documents'), 2000);
    } catch {
      setError('Failed to delete document. Please try again.');
      setDeleteDialogOpen(false);
    }
  };

  const handleDownload = async () => {
    try {
      await downloadDocumentFile(id, documentData.document_name);
      setSuccessMessage('Document downloaded successfully!');
    } catch (error) {
      setError(error.message || 'Failed to download document. Please try again.');
    }
  };

  const handleOpenInTab = async () => {
    try {
      if (documentPreviewUrl?.startsWith('blob:')) {
        openBlobUrlInNewTab(documentPreviewUrl);
        return;
      }
      await openDocumentInNewTab(id);
    } catch (error) {
      setError(error.message || 'Failed to open document. Please try again.');
    }
  };

  const getPublicityIcon = (p) => ({ public: <PublicIcon />, private: <PrivateIcon /> }[p] ?? <SecurityIcon />);

  if (loading) return <Loading message="Loading document details" size={56} variant="primary" />;

  if (error && !documentData) return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <FeedbackMessage message={error} severity="error"
        sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', fontWeight: 500, background: alpha('#450a0a', 0.92), border: '1px solid rgba(248,113,113,0.3)', color: '#fca5a5' }} />
    </Container>
  );

  if (!documentData) return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <FeedbackMessage message="Document not found" severity="error"
        sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', fontWeight: 500, background: alpha('#450a0a', 0.92), border: '1px solid rgba(248,113,113,0.3)', color: '#fca5a5' }} />
    </Container>
  );

  const hasDocumentFile = Boolean(documentData.document_pdf);
  const visibilityColor = documentData.publicity === 'private' ? '#F87171' : (documentData.publicity === 'specific' ? NEON2 : NEON3);
  const visibilityLabel = documentData.publicity === 'specific' ? 'SPECIFIC MEMBERS' : (documentData.publicity?.toUpperCase() || 'PUBLIC');

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 6 }}>
      <Header
        title="Document Details"
        subtitle="View document information"
        icon={DocumentIcon}
        showBackButton
        onBackClick={() => navigate(-1)}
        actionButton={
          <ActionButtons canManageDocuments={canManage} onDownload={handleDownload}
            onEdit={() => navigate(`/documents/edit/${id}`)} onDelete={() => setDeleteDialogOpen(true)} />
        }
      />

      <FeedbackMessage message={error} severity="error" onClose={() => setError(null)}
        sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', fontWeight: 500, background: alpha('#450a0a', 0.92), border: '1px solid rgba(248,113,113,0.3)', color: '#fca5a5', mb: 3 }} />
      <FeedbackMessage message={successMessage} severity="success" onClose={() => setSuccessMessage(null)}
        sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', fontWeight: 500, background: alpha(SUCCESS, 0.12), border: `1px solid ${alpha(SUCCESS, 0.25)}`, color: alpha(SUCCESS, 0.95), mb: 2 }} />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <Box sx={{
          borderRadius: '16px',
          border: `1px solid ${alpha(NEON, 0.15)}`,
          background: `linear-gradient(135deg, ${alpha(SURFACE_UP, 0.9)} 0%, ${alpha(SURFACE_MID, 0.98)} 100%)`,
          p: { xs: 2, md: 2.5 },
          animation: 'fadeUp 0.35s ease both',
          '@keyframes fadeUp': { from: { opacity: 0, transform: 'translateY(10px)' }, to: { opacity: 1, transform: 'translateY(0)' } },
        }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 2.5, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{
                width: 48, height: 48, borderRadius: '13px', flexShrink: 0,
                background: `linear-gradient(135deg, ${alpha(NEON2, 0.25)}, ${alpha(NEON, 0.15)})`,
                border: `2px solid ${alpha(NEON2, 0.3)}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <DocumentIcon sx={{ fontSize: 22, color: NEON2 }} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: '1.4rem', fontWeight: 700, fontFamily: "'Rajdhani', sans-serif", color: TEXT_BODY, letterSpacing: 0.3, lineHeight: 1.15 }}>
                  {documentData.document_name}
                </Typography>
              </Box>
            </Box>
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 0.7,
              px: 1.4, py: 0.5, borderRadius: '20px',
              background: alpha(visibilityColor, 0.1),
              border: `1px solid ${alpha(visibilityColor, 0.3)}`,
              alignSelf: 'flex-start',
            }}>
              <Box sx={{ width: 7, height: 7, borderRadius: '50%', background: visibilityColor }} />
              <Typography sx={{ fontSize: '0.75rem', fontFamily: "'DM Sans', sans-serif", fontWeight: 700, color: visibilityColor, letterSpacing: '0.05em' }}>
                {visibilityLabel}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 1.5 }}>
            <MetricCard label="Visibility" value={visibilityLabel} icon={documentData.publicity === 'public' ? PublicIcon : PrivateIcon} color={visibilityColor} />
            <MetricCard
              label="Event"
              value={documentData.event?.name}
              icon={EventIcon}
              color={NEON2}
              onClick={documentData.event?.id ? () => navigate(`/events/view/${documentData.event.id}`) : undefined}
            />
            <MetricCard label="Created By" value={documentData.user?.name} icon={PersonIcon} color={NEON} />
            <MetricCard label="Created" value={documentData.created_at ? format(new Date(documentData.created_at), 'dd MMM yyyy') : null} icon={CalendarIcon} color={NEON3} />
          </Box>
        </Box>

        {/* 3. Document Preview */}
        <DocumentPreviewSection
          documentData={documentData}
          documentPreviewUrl={documentPreviewUrl}
          previewLoading={previewLoading && hasDocumentFile}
          previewFailed={previewFailed && hasDocumentFile}
          onRetryPreview={async () => {
            await fetchDocument();
            setPreviewRetryKey((k) => k + 1);
          }}
          onOpenInTab={handleOpenInTab}
          onDownload={handleDownload}
        />
        <SystemInformationSection
          createdBy={documentData.user?.name}
          createdAt={formatSystemDateTime(documentData.created_at)}
          updatedBy={documentData.last_updater?.name}
          updatedAt={formatSystemDateTime(documentData.updated_at)}
          delay={0.22}
          accentColor={NEON}
          surfaceUp={SURFACE_UP}
          surfaceMid={SURFACE_MID}
          textBody={TEXT_BODY}
          textMuted={TEXT_MUTED}
        />
      </Box>

      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteDocument}
        itemType="document"
        itemName={documentData?.document_name}
        description="This action cannot be undone and will permanently remove the document and all associated data."
        confirmLabel="Delete Document"
      />
    </Container>
  );
};

export default DocumentDetails;
